import { Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import type ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { ColorPalette, PageSection } from '../../../models/end-client-reporting-package';
import { ChartSeries, ReportBlock } from '../report-aggregation';
import type { BlockPatch } from '../report-design-edit';

/** How long typing pauses before the text is written into the design. */
const SAVE_DELAY_MS = 400;

/**
 * The few formats the AI chat is told it may write, and nothing a deck's styling would fight: no
 * fonts, colours, tables or images. Headings start at h3, since the page and the module above this
 * own the bigger ones.
 */
const EDITOR_CONFIG = {
  placeholder: 'Write here…',
  toolbar: { items: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'undo', 'redo'] },
  heading: {
    options: [
      { model: 'paragraph' as const, title: 'Paragraph', class: 'ck-heading_paragraph' },
      { model: 'heading3' as const, view: 'h3', title: 'Heading', class: 'ck-heading_heading3' },
      { model: 'heading4' as const, view: 'h4', title: 'Subheading', class: 'ck-heading_heading4' },
    ],
  },
  link: { defaultProtocol: 'https://', addTargetToExternalLinks: true },
};

/**
 * NK-344 — the Text module: formatted writing on a page, edited in place.
 *
 * Read as HTML through Angular's [innerHTML], which strips scripts, event handlers and unsafe links
 * before anything reaches the page -- the one guard that covers every way text gets in, the editor,
 * the AI chat and a hand-edited document alike, in the share-link viewer as much as in Navigator.
 *
 * The editor is imported only when a text module is first edited. A client never edits, so the
 * share-link viewer never downloads it, and nor does anyone in Navigator who only reads decks; the
 * build splits it into a chunk of its own for exactly that.
 *
 * While editing, the editor is handed the text once and then owns it: feeding each saved change back
 * in would reset the cursor on every keystroke. What is typed goes out after a short pause rather
 * than per key, since every patch recomputes the whole deck.
 */
@Component({
  selector: 'app-text-block',
  standalone: false,
  template: `
    @if (editing) {
      <div class="text-editor">
        @if (!editorReady) {
          <p class="grey4 nav-font12 mb-0" role="status">{{ editorFailed ? 'The editor couldn\\'t be loaded. Reload the page to try again.' : 'Loading editor…' }}</p>
        }
        <div #editorHost></div>
      </div>
    } @else if (html) {
      <div class="text-body" [innerHTML]="html"></div>
    } @else {
      <p class="grey4 nav-font12 mb-0">No text yet. Choose <em>Edit</em> to write it.</p>
    }
  `,
  styles: [`
    :host { display: block; }
    .text-body {
      color: var(--ecr-ink, #26414c);
      font-size: var(--ecr-font-base, 13px);
      line-height: 1.55;
    }
    .text-body :is(h3, h4) { color: var(--ecr-primary); font-weight: 700; margin: 0.75em 0 0.35em; }
    .text-body h3 { font-size: 16px; }
    .text-body h4 { font-size: 14px; }
    .text-body :first-child { margin-top: 0; }
    .text-body :last-child { margin-bottom: 0; }
    .text-body a { color: var(--ecr-secondary); text-decoration: underline; }
    .text-editor { font-size: var(--ecr-font-base, 13px); }
  `],
})
export class TextBlockComponent implements ReportBlock, OnChanges, OnDestroy {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() alignCount?: number;
  @Input() compact?: boolean;
  @Input() editing?: boolean;
  @Input() onPatch?: (patch: BlockPatch) => void;

  /** Where the editor goes. Set when edit mode draws the slot for it, cleared when it takes it away. */
  @ViewChild('editorHost') set editorHost(host: ElementRef<HTMLElement> | undefined) {
    if (host) void this.openEditor(host.nativeElement);
    else this.closeEditor();
  }

  html = '';
  editorReady = false;
  editorFailed = false;

  private editor: ClassicEditor | null = null;
  /** Bumped whenever the editor is closed, so one still loading for a slot that has gone is dropped. */
  private generation = 0;
  private pending: string | null = null;
  private timer?: ReturnType<typeof setTimeout>;

  ngOnChanges() {
    if (this.block.reportType !== 'text') return;
    this.html = this.block.reportExtraData.html ?? '';
  }

  private async openEditor(host: HTMLElement) {
    const generation = ++this.generation;
    this.editorFailed = false;
    try {
      const { default: Editor } = await import('@ckeditor/ckeditor5-build-classic');
      const editor = await Editor.create(host, EDITOR_CONFIG);
      if (generation !== this.generation) {
        await editor.destroy();
        return;
      }
      editor.setData(this.html);
      editor.model.document.on('change:data', () => this.typed(editor.getData()));
      editor.editing.view.document.on('blur', () => this.flush());
      this.editor = editor;
      this.editorReady = true;
    } catch {
      if (generation === this.generation) this.editorFailed = true;
    }
  }

  private closeEditor() {
    this.generation++;
    this.flush();
    this.editorReady = false;
    const editor = this.editor;
    this.editor = null;
    void editor?.destroy();
  }

  private typed(html: string) {
    this.pending = html;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.flush(), SAVE_DELAY_MS);
  }

  private flush() {
    clearTimeout(this.timer);
    if (this.pending === null) return;
    const html = this.pending;
    this.pending = null;
    if (html !== this.html) this.onPatch?.({ html });
  }

  // Leaving edit mode, or the page, mid-pause still keeps what was typed.
  ngOnDestroy() {
    this.closeEditor();
  }
}
